// Finanz-Planer – NUR Admin. Design „Ampel und Kurve" (23.09.2026):
// vier Kacheln, eine Kurve, drei kleine Karten, Steuern in zwei Sätzen und
// alle Posten mit genau einem Haken „mit einrechnen". Keine Potenziale mehr.

import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot,
} from 'recharts';
import { Plus, Trash2, Pencil, Loader2, X, ChevronDown, ChevronRight, Download, Upload, Settings2, Table2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getFinanceItems, upsertFinanceItem, bulkUpsertFinanceItems, deleteFinanceItem, setFinanceItemActive,
  getFinanceSettings, saveFinanceSettings, computeProjection, monthDetail, euro, typeLabels, scopeLabels, intervalLabels,
  type FinanceItem, type FinanceType, type FinanceScope, type FinanceView, type FinanceSettings, type FinanceInterval,
  type MonthDetail, type MonthProjection, type MonthLine,
} from '@/lib/finance-api';

/** Beide geplanten Typen teilen die Raten-Mechanik (Betrag je Rate × Raten ab Monat). */
const istGeplant = (t: FinanceType): boolean => t === 'planned_income' || t === 'planned_expense';

interface FormState {
  id?: string;
  scope: FinanceScope;
  type: FinanceType;
  label: string;
  euro: string;
  interval: FinanceInterval;
  start_month: string; // 'YYYY-MM'
  end_month: string;   // 'YYYY-MM', leer = unbegrenzt
  installments: string;
}
const naechsterMonat = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const emptyForm = (scope: FinanceScope, type: FinanceType = 'recurring_income'): FormState => ({
  scope, type, label: '', euro: '', interval: 'monthly', start_month: naechsterMonat(), end_month: '', installments: '1',
});
// Achsen-Label: ab 1.000 € als „x k", darunter glatt.
const fmtAxis = (v: number | string): string => {
  const n = Number(v);
  return Math.abs(n) >= 1000 ? `${Math.round(n / 1000)} k` : String(Math.round(n));
};
const monatKurz = (ymd: string): string => new Date(ymd).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
const datumLang = (ymd: string): string => new Date(ymd).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
const eurSigned = (cents: number): string => (cents > 0 ? '+' : cents < 0 ? '−' : '') + euro(Math.abs(cents));
const heuteISO = (): string => new Date().toISOString().slice(0, 10);

// ── CSV ───────────────────────────────────────────────────────────────────────
// Deutsch-Excel-tauglich: Semikolon als Trenner, Komma als Dezimalzeichen,
// BOM damit Umlaute in Excel stimmen. Beträge ohne €-Zeichen, damit die
// Tabellenkalkulation rechnen kann.

function csvZelle(v: string): string {
  return /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
function csvBetrag(cents: number): string {
  return ((cents || 0) / 100).toFixed(2).replace('.', ',');
}
function csvDownload(dateiname: string, zeilen: string[][]) {
  const text = '﻿' + zeilen.map((r) => r.map((z) => csvZelle(z ?? '')).join(';')).join('\r\n');
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = dateiname;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
function csvParsen(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let zelle = '';
  let inQuote = false;
  const t = text.replace(/^﻿/, '');
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    if (inQuote) {
      if (c === '"') {
        if (t[i + 1] === '"') { zelle += '"'; i++; } else inQuote = false;
      } else zelle += c;
    } else if (c === '"') inQuote = true;
    else if (c === ';') { row.push(zelle); zelle = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && t[i + 1] === '\n') i++;
      row.push(zelle); rows.push(row); row = []; zelle = '';
    } else zelle += c;
  }
  if (zelle.length || row.length) { row.push(zelle); rows.push(row); }
  return rows;
}
function centsAusText(v: string): number | null {
  const s = v.replace(/[€\s]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
const SCOPE_AUS_TEXT: Record<string, FinanceScope> = {
  'geschäftlich': 'business', 'geschaeftlich': 'business', 'business': 'business',
  'privat': 'private', 'private': 'private',
};
const TYPE_AUS_TEXT: Record<string, FinanceType> = Object.fromEntries([
  ...Object.entries(typeLabels).map(([k, v]) => [v.toLowerCase(), k as FinanceType]),
  ['wiederkehrende einnahme', 'recurring_income'], ['geplante einnahme', 'planned_income'],
  ['geplante ausgabe', 'planned_expense'], ['steuerzahlung', 'tax_payment'], ['fixkosten', 'fixed_cost'],
]) as Record<string, FinanceType>;
const INTERVALL_AUS_TEXT: Record<string, FinanceInterval> = {
  monatlich: 'monthly', quartalsweise: 'quarterly', 'jährlich': 'yearly', jaehrlich: 'yearly',
};
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function spaltenIndex(kopf: string[]): (namensTeil: string) => number {
  const k = kopf.map((h) => (h || '').trim().toLowerCase());
  return (namensTeil: string) => k.findIndex((h) => h.includes(namensTeil.toLowerCase()));
}

// ── Seite ─────────────────────────────────────────────────────────────────────

export default function FinanzPage() {
  const queryClient = useQueryClient();
  const [privatMit, setPrivatMit] = useState(true);
  const view: FinanceView = privatMit ? 'all' : 'business';
  const [form, setForm] = useState<FormState | null>(null);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  const [tabelleOffen, setTabelleOffen] = useState(false);
  const [einstellungenOffen, setEinstellungenOffen] = useState(false);
  const [aufgeklappt, setAufgeklappt] = useState<Record<string, boolean>>({});

  const { data: items = [] } = useQuery({ queryKey: ['finance-items'], queryFn: getFinanceItems });
  const { data: settings } = useQuery({ queryKey: ['finance-settings'], queryFn: getFinanceSettings });

  // ── Einstellungen: Eingaben als Text, null = unverändert ────────────────
  const [sEdit, setSEdit] = useState<Partial<Record<keyof FinanceSettings, string>>>({});
  const s = (k: keyof FinanceSettings, fallback: string): string => {
    if (sEdit[k] !== undefined) return sEdit[k] as string;
    if (!settings) return fallback;
    const v = settings[k];
    if (k === 'start_balance' || k === 'reserve_balance' || k === 'vat_pot_start' || k === 'vat_input_monthly') return String((v as number) / 100);
    if (typeof v === 'boolean') return v ? 'ja' : 'nein';
    return v === null || v === undefined ? fallback : String(v);
  };
  const setS = (k: keyof FinanceSettings, v: string) => setSEdit((e) => ({ ...e, [k]: v }));
  const zahl = (t: string): number => parseFloat(t.replace(',', '.')) || 0;
  const activeSettings: FinanceSettings = {
    tax_rate: zahl(s('tax_rate', '30')),
    horizon_months: Math.min(Math.max(parseInt(s('horizon_months', '12')) || 12, 1), 36),
    start_balance: Math.round(zahl(s('start_balance', '0')) * 100),
    balance_date: s('balance_date', '') || null,
    reserve_balance: Math.round(zahl(s('reserve_balance', '0')) * 100),
    vat_enabled: s('vat_enabled', 'nein') === 'ja',
    vat_rate: zahl(s('vat_rate', '19')),
    vat_input_monthly: Math.round(zahl(s('vat_input_monthly', '0')) * 100),
    vat_interval: s('vat_interval', 'quarterly') === 'monthly' ? 'monthly' : 'quarterly',
    vat_pot_start: Math.round(zahl(s('vat_pot_start', '0')) * 100),
  };
  const settingsMut = useMutation({
    mutationFn: (x: FinanceSettings) => saveFinanceSettings(x),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance-settings'] }); setSEdit({}); toast.success('Einstellungen gespeichert'); },
    onError: (e: any) => toast.error(e.message),
  });

  // ── Posten ──────────────────────────────────────────────────────────────
  const saveMut = useMutation({
    mutationFn: (payload: Partial<FinanceItem>) => upsertFinanceItem(payload),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance-items'] }); setForm(null); toast.success('Gespeichert'); },
    onError: (e: any) => toast.error(e.message),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteFinanceItem(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance-items'] }); toast.success('Gelöscht'); },
    onError: (e: any) => toast.error(e.message),
  });
  // Der Haken: sofort in der Grafik sichtbar (optimistisch), bei Fehler zurück.
  const activeMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setFinanceItemActive(id, active),
    onMutate: async ({ id, active }) => {
      await queryClient.cancelQueries({ queryKey: ['finance-items'] });
      const vorher = queryClient.getQueryData<FinanceItem[]>(['finance-items']);
      queryClient.setQueryData<FinanceItem[]>(['finance-items'], (alt) => (alt || []).map((it) => (it.id === id ? { ...it, active } : it)));
      return { vorher };
    },
    onError: (e: any, _v, ctx) => { if (ctx?.vorher) queryClient.setQueryData(['finance-items'], ctx.vorher); toast.error(e.message); },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['finance-items'] }),
  });

  const projection = useMemo(
    () => computeProjection(items, activeSettings, view),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, view, activeSettings.tax_rate, activeSettings.horizon_months, activeSettings.start_balance, activeSettings.reserve_balance, activeSettings.vat_enabled, activeSettings.vat_rate, activeSettings.vat_input_monthly, activeSettings.vat_interval, activeSettings.vat_pot_start]
  );

  // Nur Geschäft: Grundlage für „Wofür du arbeitest" (Umsatz → Kosten → Steuer → dir bleibt)
  const projBiz = useMemo(
    () => computeProjection(items, activeSettings, 'business'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, activeSettings.tax_rate, activeSettings.horizon_months, activeSettings.start_balance, activeSettings.reserve_balance, activeSettings.vat_enabled, activeSettings.vat_rate, activeSettings.vat_input_monthly, activeSettings.vat_interval, activeSettings.vat_pot_start]
  );
  const jahresblick = useMemo(() => {
    const n = Math.min(12, projection.length, projBiz.length);
    const biz = projBiz.slice(0, n), alle = projection.slice(0, n);
    const umsatz = biz.reduce((x, p) => x + p.income, 0);
    const kosten = biz.reduce((x, p) => x + p.fixedCosts, 0);
    const steuer = biz.reduce((x, p) => x + p.taxReserve, 0);
    const gewinn = umsatz - kosten;
    const netto = gewinn - steuer;
    const gehalt = alle.reduce((x, p) => x + p.income, 0) - umsatz;
    const privat = alle.reduce((x, p) => x + p.fixedCosts, 0) - kosten;
    return { n, umsatz, kosten, gewinn, steuer, netto, gehalt, privat, uebrig: netto + gehalt - privat, von: alle[0]?.label, bis: alle[n - 1]?.label };
  }, [projection, projBiz]);

  // Kacheln
  const kontoHeute = activeSettings.start_balance;
  const finanzamt = activeSettings.vat_enabled ? activeSettings.vat_pot_start : 0;
  const frei = kontoHeute - finanzamt - activeSettings.reserve_balance;
  const naechsteUst = projection.find((p) => p.vatPaid > 0) || null;
  const uebernaechsteUst = naechsteUst ? projection.find((p) => p.vatPaid > 0 && p.key > naechsteUst.key) || null : null;
  // „Dauerhaft je Monat": der erste Monat ohne Projektraten — dort zeigt sich,
  // was ohne Einmaliges übrig bleibt. Gibt es den im Horizont nicht, der letzte.
  const dauerhaft = projection.find((p) => !p.hasPlanned) || projection[projection.length - 1] || null;
  const tief = projection.reduce<MonthProjection | null>((a, p) => (!a || p.available < a.available ? p : a), null);
  const jahr = new Date().getFullYear();
  const ruecklageSollJahr = projection.filter((p) => p.key.startsWith(String(jahr))).reduce((x, p) => x + p.taxReserve, 0);
  const hatSteuerzahlung = projection.some((p) => p.taxPaid > 0);
  const hatUst = activeSettings.vat_enabled;

  const submit = () => {
    if (!form) return;
    const cents = Math.round(parseFloat(form.euro.replace(',', '.')) * 100);
    if (!form.label.trim() || !Number.isFinite(cents) || cents <= 0) { toast.error('Bezeichnung und gültiger Betrag nötig'); return; }
    if (!istGeplant(form.type) && form.interval !== 'monthly' && !form.start_month) {
      toast.error('Quartals- und Jahresposten brauchen einen Startmonat (der Monat, in dem der Betrag anfällt)'); return;
    }
    if (form.type === 'tax_payment' && !form.start_month) { toast.error('Steuerzahlungen brauchen einen Startmonat'); return; }
    if (form.end_month && form.start_month && form.end_month < form.start_month) { toast.error('„Bis Monat" liegt vor dem Startmonat'); return; }
    const payload: Partial<FinanceItem> = {
      id: form.id,
      scope: form.scope,
      type: form.type,
      label: form.label.trim(),
      amount: cents,
      interval: istGeplant(form.type) ? 'monthly' : form.interval,
      start_month: form.start_month ? `${form.start_month}-01` : null,
      end_month: !istGeplant(form.type) && form.end_month ? `${form.end_month}-01` : null,
      installments: istGeplant(form.type) ? Math.max(1, parseInt(form.installments) || 1) : 1,
      ...(form.id ? {} : { active: true }),
    };
    saveMut.mutate(payload);
  };

  const editItem = (it: FinanceItem) => {
    setForm({
      id: it.id, scope: it.scope, type: it.type, label: it.label,
      euro: String(it.amount / 100),
      interval: it.interval || 'monthly',
      start_month: it.start_month ? it.start_month.slice(0, 7) : '',
      end_month: it.end_month ? it.end_month.slice(0, 7) : '',
      installments: String(it.installments || 1),
    });
    document.getElementById('posten')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── CSV ───────────────────────────────────────────────────────────────────
  const exportPosten = () => {
    const zeilen: string[][] = [
      ['Einstellung', 'Wert'],
      ['Steuersatz (%)', String(activeSettings.tax_rate).replace('.', ',')],
      ['Kontostand heute (EUR)', csvBetrag(activeSettings.start_balance)],
      ['Kontostand vom', activeSettings.balance_date || ''],
      ['Rücklagenkonto (EUR)', csvBetrag(activeSettings.reserve_balance)],
      ['Horizont (Monate)', String(activeSettings.horizon_months)],
      ['USt automatisch', activeSettings.vat_enabled ? 'ja' : 'nein'],
      ...(activeSettings.vat_enabled ? [
        ['USt-Satz (%)', String(activeSettings.vat_rate).replace('.', ',')],
        ['Vorsteuer-Schätzung (EUR/Monat)', csvBetrag(activeSettings.vat_input_monthly)],
        ['USt-Zahlweise', activeSettings.vat_interval === 'monthly' ? 'monatlich' : 'quartalsweise'],
        ['USt-Topf heute (EUR)', csvBetrag(activeSettings.vat_pot_start)],
      ] : []),
      [],
      ['Bereich', 'Typ', 'Bezeichnung', 'Betrag netto (EUR)', 'Intervall', 'Ab Monat', 'Bis Monat', 'Raten', 'Eingerechnet', 'ID'],
      ...items.map((it) => [
        scopeLabels[it.scope], typeLabels[it.type], it.label, csvBetrag(it.amount),
        istGeplant(it.type) ? '' : intervalLabels[it.interval || 'monthly'],
        it.start_month ? it.start_month.slice(0, 7) : '',
        !istGeplant(it.type) && it.end_month ? it.end_month.slice(0, 7) : '',
        istGeplant(it.type) ? String(it.installments || 1) : '',
        it.active ? 'ja' : 'nein',
        it.id,
      ]),
      [],
      ['Hochrechnung (berechnet — wird beim Import ignoriert)'],
      ['Monat', 'Einnahmen (EUR)', 'Ausgaben (EUR)', 'Steuerruecklage (EUR)', 'Steuerzahlung aus Topf (EUR)', 'Ueberschuss (EUR)', 'Frei verfuegbar (EUR)', 'Kontostand gesamt (EUR)', 'Steuer-Topf (EUR)', 'USt-Zahlung (EUR)', 'USt-Topf (EUR)'],
      ...projection.map((p) => [
        p.label, csvBetrag(p.income), csvBetrag(p.fixedCosts), csvBetrag(p.taxReserve), csvBetrag(p.taxPaid),
        csvBetrag(p.net), csvBetrag(p.available), csvBetrag(p.balance), csvBetrag(p.reservePot), csvBetrag(p.vatPaid), csvBetrag(p.vatPot),
      ]),
    ];
    csvDownload(`finanzen-${heuteISO()}.csv`, zeilen);
  };

  const importInputRef = useRef<HTMLInputElement>(null);
  const [importLaeuft, setImportLaeuft] = useState(false);
  const importPosten = async (datei: File) => {
    setImportLaeuft(true);
    try {
      const rows = csvParsen(await datei.text());
      const kopfIdx = rows.findIndex((r) => (r[0] || '').trim().toLowerCase() === 'bereich');
      if (kopfIdx < 0) { toast.error('Kopfzeile „Bereich;Typ;…" nicht gefunden — ist das eine Finanzen-CSV aus diesem Bereich?'); return; }

      const neu: Partial<FinanceSettings> = {};
      for (const r of rows.slice(0, kopfIdx)) {
        const k = (r[0] || '').trim().toLowerCase();
        const v = (r[1] || '').trim();
        if (!v) continue;
        const c = centsAusText(v);
        if (k.startsWith('steuersatz')) { const n = Number(v.replace(',', '.')); if (Number.isFinite(n) && n >= 0 && n <= 100) neu.tax_rate = n; }
        else if (k.startsWith('kontostand vom')) { if (/^\d{4}-\d{2}-\d{2}$/.test(v)) neu.balance_date = v; }
        else if (k.startsWith('kontostand')) { if (c !== null) neu.start_balance = c; }
        else if (k.startsWith('rücklagenkonto') || k.startsWith('ruecklagenkonto')) { if (c !== null) neu.reserve_balance = c; }
        else if (k.startsWith('horizont')) { const n = parseInt(v, 10); if (Number.isFinite(n)) neu.horizon_months = Math.min(Math.max(n, 1), 36); }
        else if (k.startsWith('ust automatisch')) neu.vat_enabled = v.toLowerCase() === 'ja';
        else if (k.startsWith('ust-satz')) { const n = Number(v.replace(',', '.')); if (Number.isFinite(n) && n >= 0 && n <= 100) neu.vat_rate = n; }
        else if (k.startsWith('vorsteuer')) { if (c !== null) neu.vat_input_monthly = c; }
        else if (k.startsWith('ust-zahlweise')) { const t = v.toLowerCase(); if (t.startsWith('monat')) neu.vat_interval = 'monthly'; else if (t.startsWith('quartal')) neu.vat_interval = 'quarterly'; }
        else if (k.startsWith('ust-topf')) { if (c !== null) neu.vat_pot_start = c; }
      }

      // Fail closed: EIN Fehler stoppt den ganzen Import.
      const ende = rows.findIndex((r, i) => i > kopfIdx && ((r[0] || '').trim().toLowerCase() === 'monat' || (r[0] || '').trim().toLowerCase().startsWith('hochrechnung')));
      const postenZeilen = rows.slice(kopfIdx + 1, ende < 0 ? undefined : ende);
      const fehler: string[] = [];
      const posten: Array<Partial<FinanceItem>> = [];
      const pIdx = spaltenIndex(rows[kopfIdx]);
      const pc = (r: string[], namensTeil: string): string => { const i = pIdx(namensTeil); return i >= 0 ? (r[i] || '').trim() : ''; };
      postenZeilen.forEach((r, i) => {
        const nr = kopfIdx + 2 + i;
        if (!r.some((c) => (c || '').trim())) return;
        const scope = SCOPE_AUS_TEXT[pc(r, 'bereich').toLowerCase()];
        const type = TYPE_AUS_TEXT[pc(r, 'typ').toLowerCase()];
        const label = pc(r, 'bezeichnung');
        const cents = centsAusText(pc(r, 'betrag'));
        const intervallText = pc(r, 'intervall').toLowerCase();
        const intervall = intervallText ? INTERVALL_AUS_TEXT[intervallText] : 'monthly';
        const monat = pc(r, 'ab monat');
        const bisMonat = pc(r, 'bis monat');
        const raten = pc(r, 'raten');
        const eingerechnet = (pc(r, 'eingerechnet') || pc(r, 'aktiv')).toLowerCase();
        const id = pc(r, 'id');
        if (!scope) fehler.push(`Zeile ${nr}: Bereich „${r[0] || ''}" unbekannt (erlaubt: Geschäftlich, Privat)`);
        if (!type) fehler.push(`Zeile ${nr}: Typ „${r[1] || ''}" unbekannt`);
        if (!label) fehler.push(`Zeile ${nr}: Bezeichnung fehlt`);
        if (cents === null || cents <= 0) fehler.push(`Zeile ${nr}: Betrag „${r[3] || ''}" ungültig`);
        if (monat && !/^\d{4}-\d{2}$/.test(monat)) fehler.push(`Zeile ${nr}: Ab Monat „${monat}" muss YYYY-MM sein`);
        if (bisMonat && !/^\d{4}-\d{2}$/.test(bisMonat)) fehler.push(`Zeile ${nr}: Bis Monat „${bisMonat}" muss YYYY-MM sein`);
        if (monat && bisMonat && /^\d{4}-\d{2}$/.test(bisMonat) && bisMonat < monat) fehler.push(`Zeile ${nr}: Bis Monat „${bisMonat}" liegt vor dem Startmonat`);
        if (type === 'tax_payment' && !monat) fehler.push(`Zeile ${nr}: Steuerzahlungen brauchen einen Startmonat`);
        if (intervallText && !intervall) fehler.push(`Zeile ${nr}: Intervall „${intervallText}" unbekannt (monatlich/quartalsweise/jährlich)`);
        if (intervall && intervall !== 'monthly' && type && !istGeplant(type) && !monat) fehler.push(`Zeile ${nr}: Quartals- und Jahresposten brauchen einen Startmonat`);
        if (id && !UUID_RE.test(id)) fehler.push(`Zeile ${nr}: ID „${id}" ist keine gültige ID`);
        if (eingerechnet && !['ja', 'nein'].includes(eingerechnet)) fehler.push(`Zeile ${nr}: Eingerechnet „${eingerechnet}" muss ja oder nein sein`);
        if (fehler.length) return;
        posten.push({
          ...(id ? { id } : {}),
          scope, type, label,
          amount: cents as number,
          interval: type && istGeplant(type) ? 'monthly' : (intervall || 'monthly'),
          start_month: monat ? `${monat}-01` : null,
          end_month: type && !istGeplant(type) && bisMonat ? `${bisMonat}-01` : null,
          installments: type && istGeplant(type) ? Math.max(1, parseInt(raten, 10) || 1) : 1,
          active: eingerechnet !== 'nein',
        });
      });
      if (fehler.length) {
        toast.error(`Import abgebrochen — nichts geändert.\n\n${fehler.slice(0, 5).join('\n')}${fehler.length > 5 ? `\n… und ${fehler.length - 5} weitere` : ''}`, { duration: 12000 });
        return;
      }
      if (!posten.length) { toast.error('Keine Posten in der Datei gefunden.'); return; }
      const bekannteIds = new Set(items.map((it) => it.id));
      const aktualisiert = posten.filter((p) => p.id && bekannteIds.has(p.id as string)).length;
      const neuAngelegt = posten.length - aktualisiert;
      const einstellungText = Object.keys(neu).length ? `Einstellungen: ${Object.keys(neu).length} Werte` : null;
      const zusammenfassung = [`${aktualisiert} Posten werden aktualisiert`, `${neuAngelegt} neu angelegt`, einstellungText].filter(Boolean).join('\n· ');
      if (!window.confirm(`CSV übernehmen?\n\n· ${zusammenfassung}\n\nGelöscht wird nichts.`)) return;
      await bulkUpsertFinanceItems(posten);
      if (Object.keys(neu).length) {
        await saveFinanceSettings({ ...activeSettings, ...neu });
        setSEdit({});
        queryClient.invalidateQueries({ queryKey: ['finance-settings'] });
      }
      queryClient.invalidateQueries({ queryKey: ['finance-items'] });
      toast.success(`Import übernommen — ${aktualisiert} aktualisiert, ${neuAngelegt} neu`);
    } catch (e: any) {
      toast.error(e?.message || 'Import fehlgeschlagen');
    } finally {
      setImportLaeuft(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  // ── Gruppen für die Posten-Liste ──────────────────────────────────────────
  const gruppen: Array<{ key: string; titel: string; liste: FinanceItem[]; einmal: boolean; neu: () => FormState }> = [
    { key: 'rein', titel: 'Kommt rein – jeden Monat', liste: items.filter((i) => i.type === 'recurring_income'), einmal: false, neu: () => emptyForm('business', 'recurring_income') },
    { key: 'projekte', titel: 'Kommt rein – Projekte und Einmaliges', liste: items.filter((i) => i.type === 'planned_income'), einmal: true, neu: () => emptyForm('business', 'planned_income') },
    { key: 'raus-b', titel: 'Geht raus – Geschäft', liste: items.filter((i) => (i.type === 'fixed_cost' || i.type === 'planned_expense') && i.scope === 'business'), einmal: false, neu: () => emptyForm('business', 'fixed_cost') },
    { key: 'raus-p', titel: 'Geht raus – Privat', liste: items.filter((i) => (i.type === 'fixed_cost' || i.type === 'planned_expense') && i.scope === 'private'), einmal: false, neu: () => emptyForm('private', 'fixed_cost') },
    { key: 'steuer', titel: 'Steuerzahlungen – aus dem Topf', liste: items.filter((i) => i.type === 'tax_payment'), einmal: false, neu: () => emptyForm('business', 'tax_payment') },
  ].filter((g) => g.key !== 'steuer' || g.liste.length > 0);

  const chartDaten = projection.map((p) => ({ label: p.label, freiE: p.available / 100, kontoE: p.balance / 100 }));

  return (
    <div className="space-y-5">
      {/* Kopf */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finanzen</h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeSettings.balance_date ? `Kontostand vom ${datumLang(activeSettings.balance_date)}` : 'Kontostand ohne Datum'} · Ein Haken je Posten entscheidet, ob er mitgerechnet wird. Alle Beträge netto.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 mr-2 cursor-pointer select-none">
            <input type="checkbox" checked={privatMit} onChange={(e) => setPrivatMit(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            Privates mitrechnen
          </label>
          <button onClick={() => setTabelleOffen((o) => !o)} className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium hover:bg-gray-50 ${tabelleOffen ? 'border-gray-900 text-gray-900' : 'border-gray-300 text-gray-700'}`}>
            <Table2 className="w-3.5 h-3.5" /> Tabelle
          </button>
          <button onClick={() => setEinstellungenOffen((o) => !o)} className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium hover:bg-gray-50 ${einstellungenOffen ? 'border-gray-900 text-gray-900' : 'border-gray-300 text-gray-700'}`}>
            <Settings2 className="w-3.5 h-3.5" /> Einstellungen
          </button>
          <button onClick={exportPosten} title="Alle Posten plus Einstellungen als CSV — bearbeiten und wieder hochladen aktualisiert dieselben Posten."
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <input ref={importInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void importPosten(f); }} />
          <button onClick={() => importInputRef.current?.click()} disabled={importLaeuft} title="Bearbeitete CSV wieder einlesen. Posten mit ID werden aktualisiert, Zeilen ohne ID neu angelegt. Gelöscht wird nichts."
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {importLaeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Hochladen
          </button>
        </div>
      </div>

      {/* Einstellungen (zugeklappt, bis man sie braucht) */}
      {einstellungenOffen && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Feld label="Kontostand heute (€)" hint="Alle Konten zusammen, so wie sie heute stehen. Die Hochrechnung beginnt mit dem nächsten Monat.">
              <input value={s('start_balance', '0')} onChange={(e) => setS('start_balance', e.target.value)} inputMode="decimal" className={eingabe} />
            </Feld>
            <Feld label="Stand vom">
              <input type="date" value={s('balance_date', '')} onChange={(e) => setS('balance_date', e.target.value)} className={eingabe} />
            </Feld>
            <Feld label="Rücklagenkonto (€)" hint="Was auf dem Steuerrücklagen-Konto wirklich liegt. Zählt nicht als frei.">
              <input value={s('reserve_balance', '0')} onChange={(e) => setS('reserve_balance', e.target.value)} inputMode="decimal" className={eingabe} />
            </Feld>
            <Feld label="Steuersatz Rücklage (%)" hint="Anteil vom Geschäftsgewinn, der für die Einkommensteuer zur Seite geht.">
              <input value={s('tax_rate', '30')} onChange={(e) => setS('tax_rate', e.target.value)} inputMode="decimal" className={eingabe} />
            </Feld>
            <Feld label="Umsatzsteuer automatisch">
              <select value={s('vat_enabled', 'nein')} onChange={(e) => setS('vat_enabled', e.target.value)} className={eingabe}>
                <option value="ja">ja</option><option value="nein">nein</option>
              </select>
            </Feld>
            <Feld label="Dem Finanzamt gehört heute (€)" hint="Umsatzsteuer, die schon vereinnahmt, aber noch nicht abgeführt ist. Geht im nächsten Zahlmonat raus.">
              <input value={s('vat_pot_start', '0')} onChange={(e) => setS('vat_pot_start', e.target.value)} inputMode="decimal" className={eingabe} />
            </Feld>
            <Feld label="Vorsteuer je Monat (€)" hint="Abziehbare Vorsteuer aus deutschen Rechnungen, als Pauschale. Auslandsleistungen gehören nicht hierhin.">
              <input value={s('vat_input_monthly', '0')} onChange={(e) => setS('vat_input_monthly', e.target.value)} inputMode="decimal" className={eingabe} />
            </Feld>
            <Feld label="USt-Zahlweise">
              <select value={s('vat_interval', 'quarterly')} onChange={(e) => setS('vat_interval', e.target.value)} className={eingabe}>
                <option value="quarterly">quartalsweise (Jan/Apr/Jul/Okt)</option><option value="monthly">monatlich</option>
              </select>
            </Feld>
            <Feld label="USt-Satz (%)">
              <input value={s('vat_rate', '19')} onChange={(e) => setS('vat_rate', e.target.value)} inputMode="decimal" className={eingabe} />
            </Feld>
            <Feld label="Hochrechnung (Monate)">
              <input value={s('horizon_months', '12')} onChange={(e) => setS('horizon_months', e.target.value)} inputMode="numeric" className={eingabe} />
            </Feld>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={() => settingsMut.mutate(activeSettings)} disabled={settingsMut.isPending || Object.keys(sEdit).length === 0}
              className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60">
              Speichern
            </button>
            {Object.keys(sEdit).length > 0 && <button onClick={() => setSEdit({})} className="text-xs text-gray-500 hover:text-gray-800">Änderungen verwerfen</button>}
            <p className="text-xs text-gray-400">Grobe Planung, keine Steuerberatung.</p>
          </div>
        </div>
      )}

      {/* Vier Kacheln */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kachel eyebrow="Auf dem Konto heute" wert={euro(kontoHeute)} note="alle Konten zusammen" />
        <Kachel eyebrow="Gehört dem Finanzamt" wert={euro(finanzamt + activeSettings.reserve_balance)} farbe="text-amber-700"
          note={naechsteUst ? <>Umsatzsteuer, fällig <strong>10. {naechsteUst.labelLong.split(' ')[0]}</strong>{activeSettings.reserve_balance > 0 ? ' + Rücklagenkonto' : ''}</> : 'keine Umsatzsteuer eingeplant'} />
        <Kachel eyebrow="Wirklich frei" wert={euro(frei)} note="Konto minus Finanzamt" farbe={frei >= 0 ? 'text-gray-900' : 'text-red-700'} />
        <Kachel eyebrow={dauerhaft ? `Ab ${dauerhaft.labelLong}, je Monat` : 'Je Monat'} wert={dauerhaft ? eurSigned(dauerhaft.net) : '–'}
          farbe={dauerhaft && dauerhaft.net >= 0 ? 'text-emerald-700' : 'text-red-700'}
          note={dauerhaft ? (dauerhaft.net >= 0 ? 'bleibt jeden Monat übrig, nach Steuer' : 'fehlt jeden Monat, nach Steuer') : ''} />
      </div>

      {/* Kurve */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-gray-900">So entwickelt sich dein Konto</h2>
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-[3px] rounded bg-emerald-600" /> wirklich frei</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-[3px] rounded bg-blue-500" /> Konto gesamt, inkl. Steuer in den Töpfen</span>
            {hatUst && <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-amber-600" /> Umsatzsteuer geht raus</span>}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartDaten} margin={{ top: 28, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={48} tickFormatter={fmtAxis} />
            <Tooltip formatter={(v: any, n: any) => [new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(v)), n]}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
            <Area type="monotone" dataKey="freiE" name="wirklich frei" stroke="#047857" strokeWidth={2.5} fill="#047857" fillOpacity={0.12} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="kontoE" name="Konto gesamt" stroke="#2563eb" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
            {projection.filter((p) => p.vatPaid > 0).map((p) => (
              <ReferenceDot key={p.key} x={p.label} y={p.available / 100} r={5} fill="#b45309" stroke="#ffffff" strokeWidth={2}
                label={{ value: `−${(p.vatPaid / 100000).toFixed(1).replace('.', ',')} k USt`, position: 'top', fontSize: 11, fill: '#b45309', fontWeight: 600 }} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
        {tief && (
          <p className="text-sm text-gray-600 mt-1">
            Tiefster Punkt beim frei verfügbaren Geld: <strong className={tief.available >= 0 ? 'text-gray-900' : 'text-red-700'}>{euro(tief.available)}</strong> im {tief.labelLong}.
            Die gestrichelte Linie enthält Steuer, die noch nicht abgeflossen ist.
          </p>
        )}
      </div>

      {/* Wofür du arbeitest: vom Umsatz bis zu dem, was bleibt */}
      {jahresblick.n > 0 && jahresblick.umsatz > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-900">Wofür du arbeitest</h2>
            <span className="text-xs text-gray-500">{jahresblick.von} bis {jahresblick.bis}, so wie es angehakt ist{privatMit ? '' : ' (ohne Privates)'}</span>
          </div>
          <div className="mt-3 flex h-9 w-full overflow-hidden rounded-lg text-[11px] font-semibold text-white">
            {([
              ['Kosten', jahresblick.kosten, 'bg-red-500'],
              ['Steuer', jahresblick.steuer, 'bg-amber-500'],
              ...(privatMit ? [['Privat', Math.max(0, jahresblick.privat - jahresblick.gehalt), 'bg-yellow-400 text-yellow-900'] as [string, number, string]] : []),
              ['Bleibt', privatMit ? jahresblick.uebrig : jahresblick.netto, 'bg-emerald-600'],
            ] as Array<[string, number, string]>).filter(([, v]) => v > 0).map(([name, v, cls]) => (
              <div key={name} className={`flex items-center justify-center px-1 ${cls}`} style={{ width: `${Math.max(3, (v / jahresblick.umsatz) * 100)}%` }} title={`${name}: ${euro(v)}`}>
                <span className="truncate">{name} {euro(v)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <Schritt label="Umsatz" wert={jahresblick.umsatz} />
            <Schritt label="Kosten Geschäft" wert={-jahresblick.kosten} />
            <Schritt label="Gewinn" wert={jahresblick.gewinn} fett />
            <Schritt label={`Steuer (${activeSettings.tax_rate} %)`} wert={-jahresblick.steuer} />
            <Schritt label="Dir bleibt netto" wert={jahresblick.netto} fett gruen />
          </div>
          <p className="mt-3 text-sm text-gray-700">
            Dein Geschäft zahlt dir <strong>{euro(Math.round(jahresblick.netto / jahresblick.n))} netto im Monat</strong>
            {privatMit && jahresblick.gehalt > 0 && <>, dazu {euro(Math.round(jahresblick.gehalt / jahresblick.n))} Gehalt</>}.
            {privatMit && <> Privat gehen {euro(Math.round(jahresblick.privat / jahresblick.n))} im Monat weg. Übrig: <strong className={jahresblick.uebrig >= 0 ? 'text-emerald-700' : 'text-red-700'}>{euro(jahresblick.uebrig)}</strong> in diesen {jahresblick.n} Monaten.</>}
          </p>
        </div>
      )}

      {/* Drei kleine Karten */}
      <div className="grid md:grid-cols-3 gap-3">
        <MiniKarte titel="Kommt rein" monate={projection.slice(0, 6)} wert={(p) => p.income} farbe="bg-blue-500" />
        <MiniKarte titel="Geht raus" monate={projection.slice(0, 6)} wert={(p) => p.fixedCosts} farbe="bg-red-500" />
        <MiniKarte titel="Bleibt nach Steuer" monate={projection.slice(0, 6)} wert={(p) => p.net} farbe="bg-emerald-600" signed />
      </div>

      {/* Steuern in zwei Sätzen */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Umsatzsteuer</p>
          {hatUst && naechsteUst ? (
            <>
              <h3 className="text-sm font-semibold text-gray-900 mt-1">Nächste Zahlung: {euro(naechsteUst.vatPaid)} am 10. {naechsteUst.labelLong.split(' ')[0]}</h3>
              <Balken anteil={naechsteUst.vatPaid > 0 ? activeSettings.vat_pot_start / naechsteUst.vatPaid : 0} farbe="bg-amber-500" />
              <p className="text-sm text-gray-600">
                Dem Finanzamt gehören heute <strong>{euro(activeSettings.vat_pot_start)}</strong>
                {naechsteUst.vatPaid > activeSettings.vat_pot_start ? <>, bis zur Zahlung kommen noch <strong>{euro(naechsteUst.vatPaid - activeSettings.vat_pot_start)}</strong> dazu</> : null}.
                {uebernaechsteUst && <> Danach: <strong>10. {uebernaechsteUst.labelLong.split(' ')[0]}</strong>, voraussichtlich {euro(uebernaechsteUst.vatPaid)}.</>}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500 mt-1">Umsatzsteuer wird nicht mitgerechnet. In den Einstellungen einschalten.</p>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Einkommensteuer</p>
          <h3 className="text-sm font-semibold text-gray-900 mt-1">{activeSettings.tax_rate} % vom Gewinn gehören zur Seite gelegt</h3>
          <Balken anteil={ruecklageSollJahr > 0 ? activeSettings.reserve_balance / ruecklageSollJahr : (activeSettings.reserve_balance > 0 ? 1 : 0)} farbe="bg-emerald-600" />
          <p className="text-sm text-gray-600 flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span>Laut Plan bis Jahresende noch <strong>{euro(ruecklageSollJahr)}</strong> · auf dem Rücklagenkonto: <strong>{euro(activeSettings.reserve_balance)}</strong></span>
            {activeSettings.reserve_balance < ruecklageSollJahr && activeSettings.reserve_balance <= 0 && (
              <span className="rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-[11px] font-semibold">Rücklage fehlt</span>
            )}
          </p>
          {hatSteuerzahlung && (
            <p className="text-xs text-gray-500 mt-1">Eingeplante Steuerzahlungen gehen aus dem Topf, nicht vom frei verfügbaren Geld.</p>
          )}
        </div>
      </div>

      {/* Alle Posten */}
      <div id="posten" className="bg-white rounded-lg border border-gray-200 p-4 scroll-mt-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Alle Posten</h2>
          <span className="text-xs text-gray-500">Haken weg = wird nicht mitgerechnet, bleibt aber stehen. Zeile anfahren zum Ändern.</span>
        </div>

        {form && (
          <div className="mt-3 rounded-lg border border-primary-200 bg-primary-50/40 p-3">
            <div className="flex items-center gap-2 mb-3">
              {form.id ? <Pencil className="w-4 h-4 text-primary-600" /> : <Plus className="w-4 h-4 text-primary-600" />}
              <h3 className="text-sm font-semibold text-gray-900">{form.id ? 'Posten ändern' : 'Posten hinzufügen'}</h3>
              <button onClick={() => setForm(null)} className="ml-auto text-xs text-gray-400 hover:text-gray-700 inline-flex items-center gap-1"><X className="w-3 h-3" /> Abbrechen</button>
            </div>
            <div className="grid md:grid-cols-6 gap-3">
              <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value as FinanceScope })} className={eingabe}>
                <option value="business">Geschäftlich</option>
                <option value="private">Privat</option>
              </select>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as FinanceType })} className={`${eingabe} md:col-span-2`}>
                <option value="recurring_income">Kommt rein – jeden Monat</option>
                <option value="planned_income">Kommt rein – Projekt / Einmalig</option>
                <option value="fixed_cost">Geht raus – jeden Monat</option>
                <option value="planned_expense">Geht raus – einmalig / Raten</option>
                <option value="tax_payment">Steuerzahlung aus dem Topf</option>
              </select>
              <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Bezeichnung" className={`${eingabe} md:col-span-2`} />
              <input value={form.euro} onChange={(e) => setForm({ ...form, euro: e.target.value })} inputMode="decimal"
                placeholder={istGeplant(form.type) ? '€ netto je Rate' : form.interval === 'yearly' ? '€ netto / Jahr' : form.interval === 'quarterly' ? '€ netto / Quartal' : '€ netto / Monat'}
                className={eingabe} />
            </div>
            <div className="grid md:grid-cols-6 gap-3 mt-3">
              <Feld label="Ab Monat">
                <input type="month" value={form.start_month} onChange={(e) => setForm({ ...form, start_month: e.target.value })} className={eingabe} />
              </Feld>
              {istGeplant(form.type) ? (
                <Feld label="Anzahl Raten" hint="1 = Einmalzahlung. Betrag gilt je Rate.">
                  <input value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} inputMode="numeric" className={eingabe} />
                </Feld>
              ) : (
                <>
                  <Feld label="Rhythmus">
                    <select value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value as FinanceInterval })} className={eingabe}>
                      <option value="monthly">monatlich</option>
                      <option value="quarterly">quartalsweise</option>
                      <option value="yearly">jährlich</option>
                    </select>
                  </Feld>
                  <Feld label="Bis Monat" hint="leer = läuft weiter">
                    <input type="month" value={form.end_month} onChange={(e) => setForm({ ...form, end_month: e.target.value })} className={eingabe} />
                  </Feld>
                </>
              )}
              <div className="md:col-span-3 flex items-end">
                <button onClick={submit} disabled={saveMut.isPending} className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2">
                  {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {form.id ? 'Änderungen speichern' : 'Hinzufügen'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-x-8 gap-y-6 mt-4">
          {gruppen.map((g) => {
            const offen = aufgeklappt[g.key] || g.liste.length <= 8;
            const sichtbar = offen ? g.liste : g.liste.slice(0, 8);
            const summe = g.liste.filter((i) => i.active).reduce((x, i) => x + (g.einmal ? i.amount * Math.max(1, i.installments || 1) : (i.interval === 'yearly' ? i.amount / 12 : i.interval === 'quarterly' ? i.amount / 3 : i.amount)), 0);
            return (
              <div key={g.key}>
                <div className="flex items-baseline justify-between mb-1">
                  <h3 className="text-sm font-semibold text-gray-900">{g.titel}</h3>
                  <span className="text-xs text-gray-500 tabular-nums">{euro(summe)}{g.einmal ? ' gesamt' : ' / Monat'}</span>
                </div>
                {g.liste.length === 0 && <p className="text-xs text-gray-400 py-2">Noch nichts erfasst.</p>}
                {sichtbar.map((it) => (
                  <div key={it.id} className={`group grid grid-cols-[22px_1fr_auto_auto] items-center gap-2.5 px-2 py-1.5 rounded-lg border-b border-gray-50 hover:bg-gray-50 ${it.active ? '' : 'opacity-50'}`}>
                    <input type="checkbox" checked={it.active} onChange={(e) => activeMut.mutate({ id: it.id, active: e.target.checked })}
                      title={it.active ? 'Wird mitgerechnet' : 'Wird nicht mitgerechnet'}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" />
                    <span className="min-w-0 flex items-center gap-1.5 text-sm text-gray-800">
                      <span className="truncate">{it.label}</span>
                      {it.scope === 'private' && g.key !== 'raus-p' && <span className="shrink-0 rounded-full bg-yellow-50 text-yellow-800 px-1.5 py-0.5 text-[10px] font-semibold">privat</span>}
                      {it.type === 'planned_expense' && <span className="shrink-0 rounded-full bg-gray-100 text-gray-600 px-1.5 py-0.5 text-[10px] font-semibold">{it.installments > 1 ? `${it.installments} Raten` : 'einmalig'}</span>}
                    </span>
                    <span className={`text-sm font-semibold tabular-nums whitespace-nowrap ${it.active ? 'text-gray-900' : 'line-through text-gray-500'}`}>
                      {g.einmal || it.type === 'planned_expense' ? `${(it.installments || 1) > 1 ? `${it.installments} × ` : ''}${euro(it.amount)}` : euro(it.amount)}
                      {!g.einmal && it.type !== 'planned_expense' && it.interval !== 'monthly' && <span className="text-[10px] font-normal text-violet-600">{it.interval === 'yearly' ? ' /Jahr' : ' /Quartal'}</span>}
                    </span>
                    <span className="flex items-center justify-end gap-0.5 w-[118px]">
                      <span className="text-[11px] text-gray-400 whitespace-nowrap group-hover:hidden">
                        {it.start_month ? `ab ${monatKurz(it.start_month)}` : ''}{it.end_month ? ` bis ${monatKurz(it.end_month)}` : ''}
                      </span>
                      <button onClick={() => editItem(it)} className="hidden group-hover:inline-flex p-1.5 text-gray-400 hover:text-primary-600" title="Ändern"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { if (window.confirm(`„${it.label}" löschen?`)) delMut.mutate(it.id); }} className="hidden group-hover:inline-flex p-1.5 text-gray-400 hover:text-red-600" title="Löschen"><Trash2 className="w-3.5 h-3.5" /></button>
                    </span>
                  </div>
                ))}
                <div className="flex items-center gap-3 mt-2">
                  {!offen && (
                    <button onClick={() => setAufgeklappt((a) => ({ ...a, [g.key]: true }))} className="text-xs text-primary-700 hover:underline">+ {g.liste.length - 8} weitere anzeigen</button>
                  )}
                  <button onClick={() => { setForm(g.neu()); }} className="inline-flex items-center gap-1 rounded-lg border border-dashed border-gray-300 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 hover:border-gray-400">
                    <Plus className="w-3 h-3" /> Posten hinzufügen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Zahlen als Tabelle (auf Wunsch) */}
      {tabelleOffen && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-900">Monat für Monat</h2>
            <span className="text-[11px] text-gray-400">Monat anklicken für die Einzelposten</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-2 font-medium">Monat</th>
                  <th className="px-4 py-2 font-medium text-right">Kommt rein</th>
                  <th className="px-4 py-2 font-medium text-right">Geht raus</th>
                  <th className="px-4 py-2 font-medium text-right">Steuer zur Seite</th>
                  {hatSteuerzahlung && <th className="px-4 py-2 font-medium text-right text-amber-600">Steuerzahlung</th>}
                  <th className="px-4 py-2 font-medium text-right">Bleibt</th>
                  <th className="px-4 py-2 font-medium text-right">Wirklich frei</th>
                  <th className="px-4 py-2 font-medium text-right">Konto gesamt</th>
                  <th className="px-4 py-2 font-medium text-right">Steuer-Topf</th>
                  {hatUst && <th className="px-4 py-2 font-medium text-right text-sky-600">USt-Topf</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {projection.map((p, idx) => {
                  const open = expandedMonth === p.key;
                  return (
                    <Fragment key={p.key}>
                      <tr className={`cursor-pointer ${open ? 'bg-gray-50' : 'hover:bg-gray-50'}`} onClick={() => setExpandedMonth(open ? null : p.key)}>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 font-medium">
                            {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-300" />}
                            {p.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-green-700">{euro(p.income)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-red-600">−{euro(p.fixedCosts)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-amber-600">−{euro(p.taxReserve)}</td>
                        {hatSteuerzahlung && <td className="px-4 py-2 text-right tabular-nums text-amber-700">{p.taxPaid ? `−${euro(p.taxPaid)}` : '–'}</td>}
                        <td className={`px-4 py-2 text-right tabular-nums font-medium ${p.net >= 0 ? 'text-gray-900' : 'text-red-600'}`}>{eurSigned(p.net)}</td>
                        <td className={`px-4 py-2 text-right tabular-nums font-semibold ${p.available >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{euro(p.available)}</td>
                        <td className={`px-4 py-2 text-right tabular-nums ${p.balance >= 0 ? 'text-blue-700' : 'text-red-600'}`}>{euro(p.balance)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-amber-600">{euro(p.reservePot)}</td>
                        {hatUst && (
                          <td className="px-4 py-2 text-right tabular-nums text-sky-700">
                            {euro(p.vatPot)}
                            {p.vatPaid !== 0 && <div className="text-[10px] text-sky-500">{p.vatPaid > 0 ? `−${euro(p.vatPaid)} ans FA` : `+${euro(-p.vatPaid)} Erstattung`}</div>}
                          </td>
                        )}
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={8 + (hatSteuerzahlung ? 1 : 0) + (hatUst ? 1 : 0)} className="px-4 py-4 bg-gray-50">
                            <MonthDetailPanel detail={monthDetail(items, activeSettings, view, idx)} p={p} taxRatePct={activeSettings.tax_rate} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const eingabe = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white';

function Feld({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] text-gray-500 mb-1" title={hint}>{label}</label>
      {children}
    </div>
  );
}

function Kachel({ eyebrow, wert, note, farbe = 'text-gray-900' }: { eyebrow: string; wert: string; note: ReactNode; farbe?: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{eyebrow}</p>
      <p className={`mt-1 text-[28px] leading-tight font-extrabold tracking-tight tabular-nums ${farbe}`}>{wert}</p>
      <p className="mt-1 text-xs text-gray-500">{note}</p>
    </div>
  );
}

function Schritt({ label, wert, fett, gruen }: { label: string; wert: number; fett?: boolean; gruen?: boolean }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`tabular-nums ${fett ? 'text-lg font-extrabold' : 'text-base font-semibold'} ${gruen ? 'text-emerald-700' : wert < 0 ? 'text-gray-700' : 'text-gray-900'}`}>{wert < 0 ? `−${euro(-wert)}` : euro(wert)}</p>
    </div>
  );
}

function Balken({ anteil, farbe }: { anteil: number; farbe: string }) {
  const breite = Math.max(0, Math.min(1, anteil)) * 100;
  return (
    <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden my-2">
      <div className={`h-full rounded-full ${farbe}`} style={{ width: `${breite}%` }} />
    </div>
  );
}

function MiniKarte({ titel, monate, wert, farbe, signed }: { titel: string; monate: MonthProjection[]; wert: (p: MonthProjection) => number; farbe: string; signed?: boolean }) {
  if (!monate.length) return null;
  const werte = monate.map(wert);
  const max = Math.max(...werte.map((v) => Math.abs(v)), 1);
  const erster = werte[0];
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{titel}</p>
      <p className={`mt-1 text-2xl font-extrabold tabular-nums ${signed ? (erster >= 0 ? 'text-emerald-700' : 'text-red-700') : 'text-gray-900'}`}>{signed ? eurSigned(erster) : euro(erster)}</p>
      <p className="text-xs text-gray-500">im {monate[0].labelLong}</p>
      <div className="flex items-end gap-1.5 h-12 mt-2">
        {werte.map((v, i) => (
          <span key={i} className={`flex-1 rounded-t ${v < 0 && signed ? 'bg-red-500' : farbe} ${i === 0 ? '' : 'opacity-35'}`} style={{ height: `${Math.max(4, Math.abs(v) / max * 100)}%` }} title={`${monate[i].label}: ${euro(v)}`} />
        ))}
      </div>
      <div className="flex gap-1.5 mt-1">
        {monate.map((p) => <span key={p.key} className="flex-1 text-center text-[10px] text-gray-400">{p.label.split(' ')[0]}</span>)}
      </div>
    </div>
  );
}

// ── Monats-Detail (Drill-down) ───────────────────────────────────────────────

function buildAnalysis(detail: MonthDetail, p: MonthProjection): string[] {
  if (detail.incomes.length === 0 && detail.costs.length === 0 && detail.taxPayments.length === 0) {
    return ['Keine erfassten Bewegungen in diesem Monat.'];
  }
  const out: string[] = [];
  for (const r of detail.incomes.filter((l) => l.type === 'planned_income')) {
    const last = r.note?.includes('letzte');
    out.push(`Projekt „${r.label}" (${r.note}): +${euro(r.amount)}${last ? ' — danach fällt diese Einnahme weg.' : '.'}`);
  }
  if (detail.incomes.length) {
    const top = [...detail.incomes].sort((a, b) => b.amount - a.amount)[0];
    out.push(`Größte Einnahme: „${top.label}" (+${euro(top.amount)} von ${euro(detail.incomeTotal)} gesamt).`);
  }
  if (detail.costTotal > 0) out.push(`Ausgaben gesamt: −${euro(detail.costTotal)}.`);
  if (detail.taxReserve > 0) out.push(`Steuer zur Seite: −${euro(detail.taxReserve)} (auf Geschäftsgewinn ${euro(detail.bizProfit)}).`);
  if (p.vatPaid > 0) out.push(`Umsatzsteuer: −${euro(p.vatPaid)} ans Finanzamt — reines Durchreichen, das frei Verfügbare ändert sich nicht.`);
  for (const z of detail.taxPayments) {
    out.push(`Steuerzahlung „${z.label}"${z.note ? ` (${z.note})` : ''}: −${euro(z.amount)} aus dem Topf — Topf danach ${euro(p.reservePot)}.${p.reservePot < 0 ? ' Achtung: Der Topf ist damit überzogen.' : ''}`);
  }
  out.push(detail.net >= 0
    ? `Bleibt: +${euro(detail.net)} → wirklich frei danach ${euro(p.available)}.`
    : `Monatsminus: ${euro(detail.net)} → wirklich frei danach ${euro(p.available)}.`);
  return out;
}

function MonthLineRow({ line, sign }: { line: MonthLine; sign: '+' | '−' }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <span className="flex items-center gap-1.5 min-w-0">
        <span className={`w-2 h-2 rounded-sm shrink-0 ${line.scope === 'business' ? 'bg-blue-400' : 'bg-yellow-400'}`} />
        <span className="truncate text-gray-700">{line.label}</span>
        {line.note && <span className="text-[10px] text-gray-400 shrink-0">· {line.note}</span>}
      </span>
      <span className={`tabular-nums shrink-0 ${sign === '+' ? 'text-green-700' : 'text-red-600'}`}>{sign}{euro(line.amount)}</span>
    </div>
  );
}

function MonthDetailPanel({ detail, p, taxRatePct }: { detail: MonthDetail; p: MonthProjection; taxRatePct: number }) {
  const analysis = buildAnalysis(detail, p);
  return (
    <div className="grid md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Kommt rein</h4>
        {detail.incomes.length === 0 ? <p className="text-xs text-gray-400 py-1">nichts</p> : detail.incomes.map((l) => <MonthLineRow key={l.id} line={l} sign="+" />)}
        <div className="flex justify-between border-t border-gray-200 mt-1 pt-1 font-medium">
          <span>Summe</span><span className="tabular-nums text-green-700">+{euro(detail.incomeTotal)}</span>
        </div>
      </div>
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Geht raus &amp; Steuer</h4>
        {detail.costs.length === 0 ? <p className="text-xs text-gray-400 py-1">keine Ausgaben</p> : detail.costs.map((l) => <MonthLineRow key={l.id} line={l} sign="−" />)}
        <div className="flex items-center justify-between py-1">
          <span className="flex items-center gap-1.5 text-gray-700 min-w-0">
            <span className="w-2 h-2 rounded-sm bg-amber-300 shrink-0" />
            Steuer zur Seite <span className="text-[10px] text-gray-400 truncate">· {taxRatePct} % × Gewinn {euro(detail.bizProfit)}</span>
          </span>
          <span className="tabular-nums text-amber-600 shrink-0">−{euro(detail.taxReserve)}</span>
        </div>
        <div className="flex justify-between border-t border-gray-200 mt-1 pt-1 font-medium">
          <span>Bleibt</span><span className={`tabular-nums ${detail.net >= 0 ? 'text-gray-900' : 'text-red-600'}`}>{eurSigned(detail.net)}</span>
        </div>
        {detail.taxPayments.length > 0 && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2">
            <h4 className="text-[11px] font-semibold text-amber-700 uppercase tracking-wider mb-1">Aus dem Steuer-Topf gezahlt</h4>
            {detail.taxPayments.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-0.5 text-[13px]">
                <span className="text-amber-800 min-w-0 truncate">{l.label}{l.note ? <span className="text-[10px] text-amber-500"> · {l.note}</span> : null}</span>
                <span className="tabular-nums text-amber-700 shrink-0">−{euro(l.amount)}</span>
              </div>
            ))}
          </div>
        )}
        {(p.vatAccrued !== 0 || p.vatPaid !== 0) && (
          <div className="mt-3 rounded-lg bg-sky-50 border border-sky-200 px-2.5 py-2">
            <h4 className="text-[11px] font-semibold text-sky-700 uppercase tracking-wider mb-1">Umsatzsteuer</h4>
            {p.vatAccrued !== 0 && (
              <div className="flex items-center justify-between py-0.5 text-[13px]">
                <span className="text-sky-800">vereinnahmt, abzüglich Vorsteuer</span>
                <span className="tabular-nums text-sky-700 shrink-0">+{euro(p.vatAccrued)}</span>
              </div>
            )}
            {p.vatPaid !== 0 && (
              <div className="flex items-center justify-between py-0.5 text-[13px]">
                <span className="text-sky-800">{p.vatPaid > 0 ? 'ans Finanzamt' : 'Erstattung vom Finanzamt'}</span>
                <span className="tabular-nums text-sky-700 shrink-0">{p.vatPaid > 0 ? `−${euro(p.vatPaid)}` : `+${euro(-p.vatPaid)}`}</span>
              </div>
            )}
            <p className="mt-1 text-[10px] text-sky-600">Topf am Monatsende {euro(p.vatPot)} — läuft nie über das frei Verfügbare</p>
          </div>
        )}
      </div>
      <div className="md:col-span-2 border-t border-gray-200 pt-3">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">In Worten</h4>
        <ul className="list-disc pl-4 space-y-0.5 text-gray-600 text-[13px]">
          {analysis.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
        <p className="mt-2 text-xs text-gray-500">
          Ende {p.label}: wirklich frei <strong className="text-emerald-700">{euro(p.available)}</strong> · Konto gesamt <strong className="text-blue-700">{euro(p.balance)}</strong> · Steuer-Topf <strong className="text-amber-600">{euro(p.reservePot)}</strong>
        </p>
      </div>
    </div>
  );
}
